import { getDB } from "./schema.js";

// Local data export/import — a JSON snapshot of the device's collection. A safety
// net (e.g. before a schema upgrade or the cloud migration) and a general "own
// your data" feature. The transient `pending_operations` outbox is intentionally
// excluded; only durable user data is captured.

const FORMAT = "media-tracker-backup";
// `tv`/`anime` are durable user data; their `*_catalog` stores are re-fetchable
// caches (excluded, like the transient `pending_operations` outbox). Same for the
// comics caches; followed creators are user data and are included.
const STORES = ["games", "books", "tv", "anime", "movies", "comics", "groups", "rankings", "followed_people", "series_subscriptions", "history"];

export async function exportData() {
	const db = await getDB();
	const stores = {};
	for (const store of STORES) {
		// A store may not exist on very old DBs; guard so export never throws.
		stores[store] = db.objectStoreNames.contains(store)
			? await db.getAll(store)
			: [];
	}
	return {
		format: FORMAT,
		schemaVersion: db.version,
		exportedAt: new Date().toISOString(),
		stores,
	};
}

export function isBackup(data) {
	return !!data && data.format === FORMAT && !!data.stores;
}

export function backupCounts(data) {
	const stores = data?.stores ?? {};
	return STORES.reduce((acc, s) => acc + (stores[s]?.length ?? 0), 0);
}

// Restore a backup. `replace` (default) clears each store before loading so the
// device ends up exactly matching the file; this is a local restore only — it
// does not push to the cloud (a subsequent sign-in/refresh reconciles).
export async function importData(data, { replace = true } = {}) {
	if (!isBackup(data)) {
		throw new Error("That doesn't look like a MediaTracker backup file.");
	}
	// Backups from before DB v21 carry comic follows as followed_creators.
	if (!data.stores.followed_people && Array.isArray(data.stores.followed_creators)) {
		data = {
			...data,
			stores: {
				...data.stores,
				followed_people: data.stores.followed_creators.map((c) => ({ ...c, person_key: c.creator_key, media_type: "comic" })),
			},
		};
	}
	const db = await getDB();
	let restored = 0;
	for (const store of STORES) {
		const records = data.stores[store];
		if (!Array.isArray(records)) continue;
		if (!db.objectStoreNames.contains(store)) continue;
		if (replace) await db.clear(store);
		for (const rec of records) {
			await db.put(store, rec);
			restored++;
		}
	}
	return restored;
}
